import { StatusData } from "../shared/types";
import AlternativeAdapter from "../utils/Adapters/AlternativeAdapter";
import CoinGeckoAdapter from "../utils/Adapters/CoinGeckoAdapter";
import MempoolSpaceAdapter from "../utils/Adapters/MempoolSpaceAdapter";

const tempMockData = [
  {
    name: "Microsoft (MSFT)",
    btc10y: 4917,
    btc5y: 224,
    btcNow: 186,
  },
  {
    name: "Apple (AAPL)",
    btc10y: 5665,
    btc5y: 306,
    btcNow: 299,
  },
  {
    name: "Tesla (TSLA)",
    btc10y: 1194,
    btc5y: 227,
    btcNow: 186,
  },
  {
    name: "Oracle (ORCL)",
    btc10y: 20960,
    btc5y: 397,
    btcNow: 470,
  },
]

export default class StatusDataLoader {
  private static statusData: StatusData;
  private static readonly EMPTY_MINING = {
    difficulty: 0,
    hashrate: 0,
  };
  private static readonly EMPTY_NETWORK = {
    transactionsCountInCurrentBlock: 0,
    feesInCurrentBlock: 0,
    averageFee: 0,
    rewardInCurrentBlock: 0,
    currentBlock: 0,
  };
  private static readonly EMPTY_FEAR_AND_GREED = {
    value: 0,
    classification: "Unknown",
    hundredDayAverage: 0,
    yearAverage: 0,
  };
  private static readonly EMPTY_MARKET = {
    btcPriceUsd: 0,
    btcMarketCapUsd: 0,
  };

  public static getStatusData = async (): Promise<StatusData> => {
    if (this.statusData === undefined) {
      this.statusData = await this.fetchStatusData();
    }

    return this.statusData;
  };

  private static fetchStatusData = async (): Promise<StatusData> => {
    const [miningResult, networkResult, fearAndGreedResult, marketResult] =
      await Promise.allSettled([
        this.getMiningStatus(),
        this.getNetworkStatus(),
        this.getFearAndGreedStatus(),
        this.getMarketStatus(),
      ]);

    const mining =
      miningResult.status === "fulfilled"
        ? miningResult.value
        : this.EMPTY_MINING;
    const network =
      networkResult.status === "fulfilled"
        ? networkResult.value
        : this.EMPTY_NETWORK;
    const fearAndGreed =
      fearAndGreedResult.status === "fulfilled"
        ? fearAndGreedResult.value
        : this.EMPTY_FEAR_AND_GREED;
    const market =
      marketResult.status === "fulfilled"
        ? marketResult.value
        : this.EMPTY_MARKET;

    return {
      mining,
      network,
      fearAndGreed,
      market,
      buyingPower: tempMockData
    };
  };

  private static getMiningStatus = async (): Promise<
    NonNullable<StatusData>["mining"]
  > => {
    const miningStatus =
      (await MempoolSpaceAdapter.getBitcoinMiningStats()) as any;

    // console.log(miningStatus)

    return {
      difficulty: miningStatus.currentDifficulty,
      hashrate: miningStatus.currentHashrate,
      // blockRewardIncludingFees: miningStatus.reward_block,
    };
  };

  private static getNetworkStatus = async (): Promise<
    NonNullable<StatusData>["network"]
  > => {
    // const nodes = (await BitnodesAdapter.getSnapshots() as any)?.results?.[0]; // TODO: check if needed
    const networkStatus =
      (await MempoolSpaceAdapter.getLastBlockStats()) as any;

    return {
      // totalNodes: nodes,
      transactionsCountInCurrentBlock: networkStatus.tx_count,
      feesInCurrentBlock: networkStatus.extras.totalFees,
      averageFee: networkStatus.extras.avgFee,
      rewardInCurrentBlock: networkStatus.extras.reward,
      currentBlock: networkStatus.height,
    };
  };

  private static getFearAndGreedStatus = async (): Promise<
    NonNullable<StatusData>["fearAndGreed"]
  > => {
    const fearAndGreedData =
      (await AlternativeAdapter.getFearAndGreedStats()) as any;
    const fearAndGreedStatus = fearAndGreedData[0];

    return {
      value: parseInt(fearAndGreedStatus.value),
      classification: fearAndGreedStatus.value_classification,
      hundredDayAverage: this.getDaysAverage(fearAndGreedData, 100),
      yearAverage: this.getDaysAverage(fearAndGreedData, 365),
    };
  };

  private static getMarketStatus = async (): Promise<
    NonNullable<StatusData>["market"]
  > => {
    const bitcoinData = (await CoinGeckoAdapter.getCurrencyStats("bitcoin")) as any;
    const btcPriceUsd = Number(bitcoinData?.market_data?.current_price?.usd ?? 0);
    const btcMarketCapUsd = Number(bitcoinData?.market_data?.market_cap?.usd ?? 0);

    return {
      btcPriceUsd: Number.isFinite(btcPriceUsd) ? btcPriceUsd : 0,
      btcMarketCapUsd: Number.isFinite(btcMarketCapUsd) ? btcMarketCapUsd : 0,
    };
  };

  private static getDaysAverage = (
    data: { value: string }[],
    days: number,
  ): number => {
    const daysStats = data.slice(1, days + 1);
    let totalScore = 0;
    daysStats.forEach((element: { value: string }) => {
      totalScore += parseInt(element.value);
    });
    return totalScore / days;
  };
}
